/**
 * หมู่บ้านหยานหยู as authored data (docs/archive/YANYU.md, layout ข "the bay and shrine hill", 160 × 160):
 * a fishing village climbing from its bay past the fishermen's shrine on its 6 m stone base to the
 * plaza under the old banyan; the inn west of it, the market street east to the east gate, the hall
 * of the six schools above the market, the training ground under the western cliffs and its
 * waterfall, rice terraces stepping down south to the village gate, and outside the gates the
 * bandit hills (south-west), the wolves' pine wood (east) and the ring of red rock (south).
 *
 * Ported from the design sketch `scripts/layouts/yanyu-layout.mjs` (the review images' source), op by op,
 * in the same order. Where the sketch's levels did not walk (it was drawn for pictures, before the
 * rules read heights), this file differs, and says so at the op (search "rules:").
 *
 * Coordinates are tiles (x east, y south); one tile is one metre; levels are metres above the sea.
 * Read by `buildAuthored` (layout.ts) with its own seed: the village never depends on the world seed.
 */
import type { LayoutSpec } from '../../layout';

/** Kinds that are water (a retaining wall stops at them, a fence too). */
const WATERS = ['sea', 'pond', 'brook', 'lotus', 'paddy'] as const;
/** What a terrace's retaining wall is not built over. */
const WALL_SKIP = ['stairs', 'rock', ...WATERS] as const;

export const YANYU_LAYOUT: LayoutSpec = {
  id: 'yanyu',
  zone: 'yanyu',
  w: 160,
  h: 160,
  seed: 2026,
  keepClear: 1,
  terrain: {
    coast: [
      [0, 44],
      [14, 40],
      [18, 32],
      [21, 29],
      [27, 29],
      [29, 25],
      [31, 18],
      [33, 14],
      [37, 14],
      [41, 18],
      [45, 25],
      [48, 30],
      [56, 31],
      [62, 32],
      [96, 32],
      [98, 29],
      [104, 28],
      [120, 28],
      [122, 22],
      [128, 17],
      [140, 16],
      [150, 18],
      [160, 22],
    ],
    // The western range: its foot by rows, with the pass cut through at y 53–61.
    west: [
      [53, 15, 3, 9, 2],
      [61, 0],
      [Infinity, 15, 5, 9, 3, 98, 0.25, 6],
    ],
    east: [150, 7, 8, -3],
    south: [152, 11, 8, -3, 80, 5],
    rock: { lift: 4, rise: 2.4, max: 30, noise: 4, scale: 4, step: 1.6 },
    rows: [
      [34, 1.5],
      [66, 6, 'smooth'],
      [86, 6],
      [128, 3, 'smooth'],
      [160, 6.2],
    ],
    slopes: [
      [34, 0.14, -1, -Infinity, Infinity],
      [112, 0.26, 1, 48, Infinity],
    ],
    // The eastern cape (the arena's).
    plateaus: [{ rect: [118, 160, -Infinity, 48], level: 4.2, noise: 0.6, scale: 5 }],
    // The shrine hill, then the ridge out to the wave cape.
    rises: [[60, 50, 8, 5]],
    ridges: [{ from: [52, 44], to: [35, 18], sigma: 3.5, amp: [12, 4], n: 10 }],
    // The bandits' hills to the south-west, low rises by the lair.
    hills: [
      [24, 118, 10, 4],
      [40, 142, 9, 5],
      [22, 142, 8, 5],
      [102, 140, 9, 3],
      [134, 140, 8, 4],
    ],
    noise: [6, 0.6],
    beach: { reach: 7, base: 0.4, rise: 0.32, sand: 2.2, keep: [[26, 46, 0, 32]] },
  },
  ops: [
    // ── The western pass: a cut through the range, climbing to the warp. ──
    { name: 'pass road', shape: { rect: [2, 30, 54, 60] }, kind: 'road', level: { lerp: [15, 9], along: 'i', span: [3.5, 21.5] } },
    { name: 'pass wall north', shape: { rect: [2, 30, 53, 54] }, kind: 'rock', level: 20 },
    { name: 'pass wall south', shape: { rect: [2, 30, 60, 61] }, kind: 'rock', level: 20 },
    // ── The wave cape's tip: rocks where the waves break. ──
    {
      name: 'wave cape',
      shape: { disc: [34.5, 16.5, 3.2] },
      skip: ['sea'],
      rings: [
        { upTo: 2.4, kind: 'pave', level: 1.8 },
        { kind: 'boulder', level: 1.2 },
      ],
    },
    // ── Water: the lagoon behind its sand bar, the brooks, the islet. ──
    { name: 'sand bar', shape: { rect: [96, 121, 28, 33] }, skip: ['sea'], kind: 'sand', level: 0.6 },
    {
      name: 'lotus lagoon',
      shape: { ellipse: [108.5, 40.5, 11.5, 8.5] },
      rings: [
        { upTo: 1, kind: 'pond', level: 0.3 },
        { upTo: 1.18, kind: 'sand', level: 0.7, skip: ['sea'] },
      ],
    },
    { name: 'lagoon reeds', shape: { ellipse: [108.5, 40.5, 11.5, 8.5] }, clip: [0, 100, 0, 160], only: ['sand'], kind: 'reeds', level: 0.7 },
    { name: 'lotus', shape: { ellipse: [108.5, 40.5, 11.5, 8.5] }, minR: 0.55, speckle: 0.55, only: ['pond'], kind: 'lotus', level: 0.3 },
    { name: 'lotus bed', shape: { rect: [113, 121, 43, 50] }, only: ['pond'], kind: 'lotus', level: 0.3 },
    { name: 'lagoon mouth', shape: { rect: [104, 108, 26, 33] }, kind: 'pond', level: 0.3 },
    {
      name: 'lower brook',
      shape: {
        path: [
          [24, 64],
          [25, 56],
          [26, 47],
          [25, 38],
          [24, 30],
          [24, 26],
        ],
        width: 2.6,
      },
      skip: ['sea'],
      kind: 'brook',
      level: { lerp: [6.2, 0], along: 's', scale: 1.15, ease: 'smooth' },
    },
    {
      name: 'upper brook',
      shape: {
        path: [
          [4, 86],
          [10, 78],
          [16, 71],
          [19.5, 69],
        ],
        width: 2.4,
      },
      kind: 'brook',
      level: { lerp: [19, 14.5], along: 's' },
    },
    {
      name: 'east stream',
      shape: {
        path: [
          [156, 64],
          [142, 58],
          [130, 54],
          [119, 50],
          [113, 46],
        ],
        width: 2.4,
      },
      keepRockBefore: 0.08,
      kind: 'brook',
      level: { lerp: [9, 0.3], along: 's', ease: 'smooth' },
    },
    {
      name: 'islet',
      shape: { disc: [100.5, 6.5, 2.2] },
      rings: [
        { upTo: 1.2, kind: 'pave', level: 2.2 },
        { kind: 'boulder', level: 1.4 },
      ],
    },
    // ── The harbour: the quay, the pier and its landing stage, the fishing jetty, the arched bridge. ──
    { name: 'quay', shape: { rect: [56, 104, 32, 36] }, kind: 'pave', level: 1.5 },
    // rules: the pier rises gently from the landing stage (0.6) to the quay (1.5) instead of a flat 1.0
    { name: 'pier', shape: { rect: [79, 82, 12, 32] }, kind: 'deck', level: { lerp: [0.7, 1.45], along: 'j', span: [13, 31] } },
    { name: 'landing stage', shape: { rect: [70, 92, 9, 13] }, kind: 'deck', level: 0.6 },
    // rules: the jetty likewise rises to the quay
    { name: 'jetty', shape: { rect: [92, 94, 20, 32] }, kind: 'deck', level: { lerp: [0.9, 1.45], along: 'j', span: [20, 31] } },
    // rules: the arch over the lagoon's mouth rises 0.3 a tile from the quay's level and back down
    { name: 'arched bridge', shape: { rect: [103, 109, 29, 33] }, kind: 'deck', level: { arch: [1.5, 2.4], along: 'i', span: [103, 109] } },
    // ── The village road: quay → stone steps → the shrine's courtyard → the plaza. ──
    { name: 'village road (harbour)', shape: { rect: [78, 82, 36, 43] }, kind: 'road', level: 1.6 },
    { name: 'village steps', flight: 'stairs', rect: [78, 82, 43, 49], down: 'n', bottom: 1.6, top: 3.6, steps: 10 },
    { name: 'village road', shape: { rect: [78, 82, 49, 64] }, kind: 'road', level: { lerp: [3.8, 6], along: 'j', span: [49, 64] } },
    { name: 'plaza', shape: { rect: [68, 92, 64, 84], cut: 3 }, kind: 'pave', level: 6 },
    { name: 'west road', shape: { rect: [50, 68, 78, 82] }, skip: ['pave'], kind: 'road', level: 6 },
    { name: 'training ground steps', flight: 'stairs', rect: [48, 50, 78, 82], down: 'e', bottom: 6, top: 7, steps: 3 },
    // ── The training ground (7 m) under the western cliffs. ──
    { name: 'training ground', shape: { rect: [20, 48, 62, 90] }, keepRange: 1, kind: 'yard', level: 7, hard: true },
    { name: 'falls pool', shape: { ellipse: [23.5, 68, 4.5, 5] }, clip: [19, 28, 63, 73], kind: 'pond', level: 6.6 },
    // rules: the cliff the falls drop over, between the upper brook (14.5) and the pool (6.6): the
    // sketch left this strip as hillside under the range's foot
    { name: 'falls cliff', shape: { rect: [15, 20, 62, 76] }, skip: ['brook', 'pond'], kind: 'rock', level: 15.2 },
    { name: 'pass stairs', flight: 'stairs', rect: [30, 34, 54, 62], down: 's', bottom: 7, top: 9 },
    { name: 'pass road (east)', shape: { rect: [29, 30, 54, 60] }, skip: ['brook'], kind: 'road', level: 9 },
    { name: 'pass bridge (east end)', shape: { rect: [29, 30, 54, 60] }, only: ['brook'], kind: 'deck', level: 9, hard: true },
    { name: 'pass bridge', shape: { rect: [22, 29, 54, 60] }, kind: 'deck', level: 9, hard: true },
    // The practice wall: a terrace 3.5 m high against the south-west cliff.
    { name: 'practice wall', shape: { rect: [21, 30, 82, 89] }, kind: 'pave', level: 10.5, hard: true },
    { name: 'practice wall (wall)', shape: { around: [21, 30, 82, 89] }, skip: WALL_SKIP, kind: 'wall', level: 10.5 },
    // rules: one narrow stair at its east end, five tiles long (the sketch's was a single tile)
    { name: 'practice wall stair', flight: 'stairs', rect: [30, 35, 82, 83], down: 'e', bottom: 7, top: 10.5 },
    { name: 'sparring platform', shape: { rect: [38, 46, 74, 81] }, kind: 'deck', level: 8.2, hard: true },
    // rules: the quarry floor at 7.2 (not 7.4), so it is walked into from the yard
    { name: 'quarry', shape: { rect: [15, 24, 90, 99] }, kind: 'gravel', level: 7.2 },
    // ── The fishermen's shrine: its courtyard on the road, the temple base 6 m up. ──
    { name: 'shrine courtyard', shape: { rect: [72, 78, 48, 58] }, kind: 'court', level: 4 },
    { name: 'temple base', shape: { rect: [55, 66, 44, 62] }, kind: 'court', level: 10, hard: true },
    { name: 'temple base (wall)', shape: { around: [55, 66, 44, 62] }, skip: WALL_SKIP, kind: 'wall', level: 10 },
    { name: 'shrine stair', flight: 'stairs', rect: [66, 72, 50, 56], down: 'e', bottom: 4, top: 10, steps: 24 },
    { name: 'temple base (front)', shape: { rect: [66, 72, 44, 62] }, skip: ['stairs'], kind: 'wall', level: 10 },
    // The pagoda's rock (13 m), a short stair up from the base.
    {
      name: 'pagoda rock',
      shape: { ellipse: [50, 44.5, 5.5, 6] },
      clip: [45, 55, 39, 50],
      hard: true,
      rings: [
        { upTo: 0.78, kind: 'pave', level: 13 },
        { upTo: 1.05, kind: 'rock', level: 13.6 },
      ],
    },
    { name: 'pagoda steps', flight: 'stairs', rect: [53, 56, 46, 49], down: 'e', bottom: 10, top: 13 },
    // rules: the ridge path down to the cape is one flight from the rock (13) to the cape (1.8)
    {
      name: 'ridge path',
      shape: {
        path: [
          [47, 40],
          [42, 33],
          [38, 26],
          [35, 20],
        ],
        width: 2.2,
      },
      skip: ['sea'],
      kind: 'road',
      flight: 'stairs',
      level: { lerp: [13, 1.8], along: 's' },
      stairsAlong: [
        [0, 0.35],
        [0.6, 0.85],
      ],
    },
    // ── The elder's house terrace (8 m) and the hall of the six schools (8.5 m). ──
    { name: "elder's terrace", shape: { rect: [86, 98, 51, 61] }, kind: 'court', level: 8, hard: true },
    { name: "elder's terrace (wall)", shape: { around: [86, 98, 51, 61] }, skip: WALL_SKIP, kind: 'wall', level: 8 },
    { name: "elder's stair", flight: 'stairs', rect: [90, 95, 61, 64], down: 's', bottom: 6, top: 8 },
    { name: 'schools terrace', shape: { rect: [98, 118, 50, 64] }, kind: 'court', level: 8.5, hard: true },
    { name: 'schools terrace (wall)', shape: { around: [98, 118, 50, 64] }, skip: WALL_SKIP, kind: 'wall', level: 8.5 },
    { name: 'market street', shape: { rect: [92, 119, 66, 78] }, kind: 'pave', level: 6 },
    // rules: laid after the market street (the sketch's street paved over the stair's foot)
    { name: 'schools stair', flight: 'stairs', rect: [105, 111, 64, 68], down: 's', bottom: 6, top: 8.5 },
    // ── The south road: under the drum tower, past the fields to the gate and on to the warp. ──
    {
      name: 'south road',
      shape: { rect: [78, 82, 84, 128] },
      skip: ['rock'],
      kind: 'road',
      level: { lerp: [6, 3], along: 'j', span: [84, 128], ease: 'smooth' },
    },
    { name: 'south road (outside)', shape: { rect: [78, 82, 128, 150] }, skip: ['rock'], kind: 'road', level: { lerp: [3, 5.56], along: 'j', span: [128, 160] } },
    { name: 'south road (to the warp)', shape: { rect: [78, 82, 150, 160] }, kind: 'road', level: { lerp: [3, 5.56], along: 'j', span: [128, 160] } },
    { name: 'carriage yard', shape: { rect: [86, 98, 86, 96] }, kind: 'pave', level: 5.8 },
    { name: 'threshing floor', shape: { rect: [64, 76, 88, 95] }, kind: 'yard', level: 5.8 },
    // ── Tracks: east into the wood, along the sand bar to the arena, the field track, the south tracks. ──
    {
      name: 'east track',
      shape: {
        path: [
          [119, 72],
          [128, 74],
          [136, 76],
        ],
        width: 2.2,
      },
      skip: ['brook'],
      kind: 'road',
      level: 'ground',
    },
    // rules: the arena track follows the sand bar's ground, then climbs onto the cape by a flight of
    // steps (the sketch raised the whole track to 2.4 over the sand, under the cape's 4.5)
    {
      name: 'arena track',
      shape: {
        path: [
          [109, 30],
          [118, 30],
        ],
        width: 2.2,
      },
      kind: 'road',
      level: 'ground',
    },
    {
      name: 'arena steps',
      shape: {
        path: [
          [117, 30.2],
          [126.5, 31.5],
        ],
        width: 2.2,
      },
      kind: 'road',
      flight: 'stairs',
      level: { lerp: [0.6, 4.5], along: 's' },
      stairsAlong: [[0, 1.01]],
    },
    {
      name: 'field track',
      shape: {
        path: [
          [52, 82],
          [52, 96],
          [47, 105],
          [36, 109.5],
          [26, 114],
        ],
        width: 2,
      },
      kind: 'road',
      level: 'ground',
    },
    {
      name: 'south-west track',
      shape: {
        path: [
          [80, 131],
          [64, 134],
          [44, 134],
          [36, 133],
        ],
        width: 2,
      },
      kind: 'road',
      level: 'ground',
    },
    {
      name: 'lair track',
      shape: {
        path: [
          [80, 131],
          [98, 134],
          [110, 139],
        ],
        width: 2,
      },
      kind: 'road',
      level: 'ground',
    },
    // ── The arena on the east cape (4.5 m): a sand ring, walls and stands, the way in from the west. ──
    {
      name: 'arena',
      shape: { disc: [135.5, 31.5, 9.6] },
      rings: [
        { upTo: 6.6, kind: 'yard', level: 4.5 },
        { upTo: 8, kind: 'wall', level: 5.6, gap: { at: [Math.PI], half: 0.32, kind: 'pave', level: 4.5 } },
        { kind: 'wall', level: 6.8, gap: { at: [Math.PI], half: 0.32, kind: 'pave', level: 4.5 } },
      ],
    },
    // ── Fields: rice terraces stepping down south in bands, bunds between; the vegetable plots. ──
    {
      name: 'rice terraces',
      fields: [
        [36, 64, 98, 126],
        [84, 113, 107, 126],
        [52, 64, 86, 97],
      ],
      plot: 'paddy',
      bands: [
        [104, 5.2],
        [110, 4.6],
        [116, 4],
        [122, 3.4],
        [Infinity, 2.8],
      ],
      every: [5, 5],
      bundRows: [103, 109, 115, 121],
      bund: 0.15,
      skip: ['road', 'pave', 'yard'],
    },
    {
      name: 'vegetable plots',
      fields: [[95, 107, 97, 106]],
      plot: 'plot',
      bands: [[Infinity, 5.2]],
      every: [0, 3],
      bund: 0,
    },
    // Fences round the fields and the village's south edge, the east boundary along the wood.
    {
      name: 'village fence',
      fence: [
        [35.5, 90],
        [35.5, 127.5],
        [113.5, 127.5],
        [113.5, 96.5],
        [119.5, 96.5],
        [119.5, 52],
      ],
      gaps: [
        [34, 37, 107, 112],
        [76, 85, 125, 130],
        [118, 121, 69, 78],
      ],
      height: 1.2,
    },
    // ── Outside the village: the pine wood, bamboo, the bandit hills, the lair. ──
    {
      name: 'pine wood',
      scatter: { rect: [119, 152, 50, 152], clear: [136.5, 76.5, 4.5], rules: [{ noise: [5, 30, 5, 4, 0.38], p: 0.55, kind: 'forest' }] },
    },
    {
      name: 'western woods and bamboo',
      scatter: {
        rect: [14, 36, 32, 152],
        rules: [
          { rows: [93, 152], noise: [4, 3, 4, 50, 0.5], p: 0.45, kind: 'forest' },
          { rows: [0, 63], noise: [4, 3, 4, 50, 0.42], p: 0.6, kind: 'bamboo' },
        ],
      },
    },
    { name: 'shrine bamboo', scatter: { rect: [36, 56, 36, 64], rules: [{ noise: [3, 0, 3, 9, 0.45], p: 0.6, kind: 'bamboo' }] } },
    { name: 'east fence bamboo', scatter: { rect: [114, 119, 79, 96], rules: [{ p: 0.5, kind: 'bamboo' }] } },
    {
      name: 'south hills',
      scatter: {
        rect: [36, 150, 128, 152],
        rules: [
          { noise: [5, 70, 5, 0, 0.55], p: 0.4, kind: 'forest' },
          { p: 0.015, kind: 'boulder' },
        ],
      },
    },
    // The bandit camp: a palisade ring with its gate to the north-east.
    {
      name: 'bandit camp',
      shape: { disc: [30.5, 134.5, 8] },
      rings: [
        { upTo: 6.8, kind: 'yard', level: 8.6 },
        { kind: 'fence', level: 9.8, gap: { at: [-Math.PI / 4], half: 0.3, kind: 'road', level: 8.6 } },
      ],
    },
    // The ring of red rock (ลานเลือด), four ways in.
    {
      name: 'red ring',
      shape: { disc: [118.5, 140.5, 9] },
      rings: [
        { upTo: 6.4, kind: 'yard', level: 3.4 },
        {
          upTo: 8,
          kind: 'redrock',
          level: 7,
          gap: { at: [Math.PI, -Math.PI / 2, Math.PI / 2, 0], half: 0.22, kind: 'road', level: 3.6 },
        },
      ],
    },
    { name: 'village trees', scatter: { rect: [30, 119, 30, 128], rules: [{ p: 0.035, kind: 'forest' }] } },
    // The zigzag bridge to the lagoon pavilion, the lane along the harbour, the old banyan.
    {
      name: 'zigzag bridge',
      shape: {
        path: [
          [96, 44],
          [100, 44],
          [100, 40],
          [104, 40],
          [106, 39.5],
        ],
        width: 1.2,
      },
      only: ['sea', 'pond', 'brook', 'lotus', 'reeds'],
      kind: 'deck',
      level: 1,
      hard: true,
    },
    {
      name: 'harbour lane',
      shape: {
        path: [
          [90, 33.5],
          [96, 36],
          [96, 44],
        ],
        width: 1.6,
      },
      only: ['grass'],
      kind: 'road',
    },
    { name: 'banyan', shape: { rect: [86, 89, 66, 69] }, kind: 'banyan', level: 6 },
  ],
  buildings: [
    // Harbour
    { id: 'yanyu-home-hai', name: 'บ้านลุงไห่', kind: 'house', x0: 64, x1: 71, y0: 37, y1: 43, door: [70, 40, 'e'], eave: 5.8, rise: 2.4, roof: 'hip', district: 1 },
    { id: 'yanyu-home-2', name: 'บ้านชาวประมง', kind: 'house', x0: 85, x1: 92, y0: 37, y1: 43, door: [85, 40, 'w'], eave: 3.2, rise: 2.4, roof: 'thatch', district: 1 },
    // Annexes (lane L2, the scenery sheet's houses): a low thatched shed against a house's end wall.
    { name: 'เพิงข้างบ้านชาวประมง', kind: 'house', x0: 92, x1: 95, y0: 38, y1: 42, eave: 2.4, rise: 1.3, roof: 'thatch', district: 1 },
    { id: 'yanyu-home-3', name: 'บ้านริมหาด', kind: 'house', x0: 49, x1: 55, y0: 35, y1: 40, door: [54, 37, 'e'], eave: 3.2, rise: 2.4, roof: 'thatch', district: 1 },
    { name: 'เพิงตลาดปลา', kind: 'shed', x0: 65, x1: 75, y0: 32, y1: 35, base: 1.5, eave: 3, rise: 1.4, roof: 'thatch', district: 1 },
    { name: 'หอสังเกตการณ์ท่าเรือ', kind: 'tower', x0: 57, x1: 61, y0: 32, y1: 36, base: 1.5, eave: 9, rise: 2.5, roof: 'hip', district: 1 },
    { id: 'yanyu-watch-house', name: 'บ้านลุงเจิ้ง (ยาม)', kind: 'house', x0: 84, x1: 90, y0: 46, y1: 52, door: [84, 49, 'w'], eave: 3.2, rise: 2.4, roof: 'hip', district: 3 },
    { name: 'ศาลากลางบึงบัว', kind: 'pavilion', x0: 106, x1: 111, y0: 37, y1: 42, base: 1.2, eave: 3, rise: 2.6, roof: 'pyramid', color: 'red', district: 2 },
    // The shrine
    { id: 'yanyu-shrine', name: 'ศาลเจ้าชาวประมง (กระบี่ไผ่)', kind: 'hall', x0: 56, x1: 64, y0: 47, y1: 56, base: 10, eave: 4.6, rise: 3.6, roof: 'hip', door: [63, 51, 'e'], color: 'red', district: 3 },
    { name: 'หอระฆัง', kind: 'pavilion', x0: 57, x1: 60, y0: 57, y1: 60, base: 10, eave: 3, rise: 2, roof: 'pyramid', color: 'red', district: 3 },
    { name: 'เจดีย์ห้าชั้น', kind: 'pagoda', x0: 48, x1: 52, y0: 42, y1: 46, base: 13, eave: 18, rise: 3, roof: 'tiers', color: 'red', district: 3 },
    // Plaza
    { id: 'yanyu-inn', name: 'โรงเตี๊ยมลมทะเล', kind: 'inn', x0: 54, x1: 66, y0: 66, y1: 76, base: 6, eave: 6.4, rise: 3.2, roof: 'hip', door: [65, 71, 'e'], district: 4 },
    { name: 'หอกลอง', kind: 'drumtower', x0: 75, x1: 85, y0: 84, y1: 87, base: 5.8, eave: 7.5, rise: 3, roof: 'hip', color: 'red', axis: 'x', passage: [78, 82], district: 4 },
    { id: 'yanyu-elder-house', name: 'บ้านผู้เฒ่าหลิว', kind: 'house', x0: 88, x1: 96, y0: 52, y1: 59, base: 8, door: [92, 58, 's'], eave: 3.2, rise: 2.4, roof: 'hip', district: 4 },
    // Market and the schools
    { name: 'ศาลาหกสำนัก', kind: 'recruit', x0: 100, x1: 116, y0: 51, y1: 58, base: 8.5, eave: 4.6, rise: 3.4, roof: 'hip', color: 'red', district: 6 },
    { id: 'yanyu-shop', name: 'ร้านค้าหยานหยู', kind: 'house', x0: 93, x1: 101, y0: 79, y1: 86, base: 6, door: [97, 79, 'n'], eave: 3.2, rise: 2.4, roof: 'hip', district: 5 },
    { name: 'แผงลอย', kind: 'stall', x0: 98, x1: 101, y0: 66, y1: 68, base: 6, eave: 2.2, rise: 0.8, roof: 'awning', color: 'red', district: 5 },
    { name: 'แผงลอย', kind: 'stall', x0: 102, x1: 105, y0: 66, y1: 68, base: 6, eave: 2.2, rise: 0.8, roof: 'awning', color: 'white', district: 5 },
    { name: 'แผงลอย', kind: 'stall', x0: 112, x1: 115, y0: 66, y1: 68, eave: 2.2, rise: 0.8, roof: 'awning', color: 'blue', district: 5 },
    { name: 'แผงลอย', kind: 'stall', x0: 115, x1: 118, y0: 66, y1: 68, eave: 2.2, rise: 0.8, roof: 'awning', color: 'red', district: 5 },
    { name: 'แผงลอย', kind: 'stall', x0: 103, x1: 106, y0: 79, y1: 81, base: 6, eave: 2.2, rise: 0.8, roof: 'awning', color: 'blue', district: 5 },
    { name: 'แผงลอย', kind: 'stall', x0: 107, x1: 110, y0: 79, y1: 81, base: 6, eave: 2.2, rise: 0.8, roof: 'awning', color: 'white', district: 5 },
    { name: 'แผงลอย', kind: 'stall', x0: 111, x1: 114, y0: 79, y1: 81, eave: 2.2, rise: 0.8, roof: 'awning', color: 'red', district: 5 },
    // The shrine courtyard's east garden wall, a moon gate (月洞門) in it to the plaza's path.
    { name: 'ประตูจันทร์ลานศาล', kind: 'moongate', x0: 78, x1: 79, y0: 49, y1: 58, base: 4, eave: 3.7, roof: 'none', passage: [52, 55], district: 3 },
    { name: 'ประตูตะวันออก', kind: 'gate', x0: 119, x1: 120, y0: 69, y1: 78, base: 6.6, eave: 4.5, rise: 1.2, color: 'red', passage: [71, 74], district: 5 },
    // Training ground
    { name: 'ซุ้มประตูลานฝึก', kind: 'gate', x0: 47, x1: 48, y0: 77, y1: 83, base: 7, eave: 4, rise: 1, district: 7 },
    { name: 'โรงเก็บอาวุธ', kind: 'house', x0: 34, x1: 40, y0: 84, y1: 89, base: 7, eave: 3, rise: 2, roof: 'hip', district: 7 },
    // Fields and the south gate
    { id: 'yanyu-farm-niu', name: 'บ้านอาหนิว', kind: 'house', x0: 66, x1: 73, y0: 97, y1: 103, door: [72, 100, 'e'], eave: 3.2, rise: 2.4, roof: 'thatch', district: 8 },
    { id: 'yanyu-farm-sun', name: 'บ้านป้าซุน', kind: 'house', x0: 86, x1: 93, y0: 98, y1: 104, door: [86, 101, 'w'], eave: 3.2, rise: 2.4, roof: 'thatch', district: 8 },
    { name: 'เพิงข้างบ้านอาหนิว', kind: 'house', x0: 67, x1: 71, y0: 94, y1: 97, eave: 2.4, rise: 1.3, roof: 'thatch', district: 8 },
    { name: 'เพิงข้างบ้านป้าซุน', kind: 'house', x0: 93, x1: 96, y0: 99, y1: 103, eave: 2.4, rise: 1.3, roof: 'thatch', district: 8 },
    { name: 'ยุ้งฉาง', kind: 'granary', x0: 57, x1: 63, y0: 87, y1: 93, base: 5.8, eave: 3.4, rise: 2.4, roof: 'thatch', district: 8 },
    { name: 'โรงม้า ท่ารถม้า', kind: 'stable', x0: 89, x1: 98, y0: 86, y1: 91, base: 5.8, eave: 3.4, rise: 2.2, roof: 'hip', district: 9 },
    { name: 'ซุ้มประตูหมู่บ้าน', kind: 'paifang', x0: 75, x1: 86, y0: 127, y1: 128, base: 3, eave: 7, rise: 1.6, color: 'red', passage: [78, 83], district: 9 },
    // The guard post: its cell (the jail) open inside its walls.
    { name: 'ป้อมยามและห้องขัง', kind: 'guard', x0: 86, x1: 93, y0: 123, y1: 130, base: 3, eave: 3.4, rise: 2.2, roof: 'hip', cell: [87, 92, 124, 129], district: 9 },
    { name: 'ประตูนา', kind: 'gate', x0: 35, x1: 36, y0: 107, y1: 112, base: 4.6, eave: 4.2, rise: 0.8, district: 8 },
    // Outside
    { name: 'กระโจมโจร', kind: 'tent', x0: 26, x1: 29, y0: 131, y1: 134, base: 8.6, eave: 0.6, rise: 2.2, roof: 'tent', district: 13 },
    { name: 'กระโจมโจร', kind: 'tent', x0: 31, x1: 34, y0: 137, y1: 140, base: 8.6, eave: 0.6, rise: 2.2, roof: 'tent', district: 13 },
    { name: 'กระโจมโจร', kind: 'tent', x0: 33, x1: 36, y0: 130, y1: 133, base: 8.6, eave: 0.6, rise: 2.2, roof: 'tent', district: 13 },
    { name: 'หอดูต้นทางโจร', kind: 'tower', x0: 24, x1: 26, y0: 136, y1: 138, base: 8.6, eave: 6, rise: 1.4, roof: 'thatch', district: 13 },
    { name: 'ศาลหินบนเกาะ', kind: 'kiosk', x0: 100, x1: 102, y0: 6, y1: 8, base: 2.2, eave: 1.6, rise: 1, roof: 'pyramid', color: 'red', district: 2 },
  ],
  boats: [
    { x: 66.5, y: 14.5, len: 7, axis: 'x', kind: 'ferry', name: 'เรือข้ามฟาก' },
    { x: 87, y: 17, len: 5, axis: 'y', kind: 'boat' },
    { x: 97, y: 22, len: 5, axis: 'y', kind: 'boat' },
    { x: 58, y: 24, len: 5, axis: 'x', kind: 'boat' },
    { x: 116, y: 10, len: 5, axis: 'x', kind: 'boat' },
    { x: 112, y: 36, len: 3, axis: 'x', kind: 'skiff' },
  ],
  // The pole forest (梅花桩): posts of rising height in the yard, 8 to 10.4 m.
  posts: [0, 1, 2, 3, 4].flatMap((a) =>
    [0, 1, 2].map((b) => ({ x: 36 + a * 2 + 0.5, y: 63 + b * 2 + 0.5, h: 7 + 1 + ((a + b) % 3) * 0.8 + a * 0.3 })),
  ),
  props: [
    // Landmark trees: the old banyan, willows round the lagoon, blossom by the shrine, shade by the plaza.
    { kind: 'banyan', x: 87.5, y: 67.5, s: 1 },
    ...(
      [
        [97, 47],
        [99, 34],
        [118, 33],
        [120, 45],
        [94, 41],
        [112, 49],
      ] as const
    ).map(([x, y]) => ({ kind: 'willow', x, y, s: 1 })),
    ...(
      [
        [73, 47],
        [73, 58],
        [67, 63],
        [56, 63],
        [44, 52],
      ] as const
    ).map(([x, y]) => ({ kind: 'blossom', x, y, s: 1.2 })),
    ...(
      [
        [69, 66],
        [91, 82],
        [68, 82],
        [52, 77],
      ] as const
    ).map(([x, y]) => ({ kind: 'broad', x, y, s: 1.1 })),
    ...(
      [
        [69.5, 65.5],
        [90.5, 65.5],
        [69.5, 82.5],
        [90.5, 82.5],
        [77.5, 50.5],
        [77.5, 56.5],
        [80.5, 45],
      ] as const
    ).map(([x, y]) => ({ kind: 'lantern', x, y })),
    { kind: 'well', x: 71.5, y: 67.5 },
    { kind: 'board', x: 74, y: 64.6 },
    ...['#3b6fb6', '#d9a43a', '#2f3a52', '#c8508f', '#e8e2cf', '#a8261f'].map((c, k) => ({
      kind: 'tallBanner',
      x: 101.5 + k * 2.8,
      y: 60.5,
      c,
    })),
    ...(
      [
        [45.5, 77.5],
        [45.5, 82.5],
        [116, 70],
        [116, 77],
        [127.5, 31.5],
        [143.5, 31.5],
        [135.5, 23],
        [135.5, 40],
      ] as const
    ).map(([x, y]) => ({ kind: 'banner', x, y, c: '#b3322c' })),
    ...(
      [
        [31.5, 71.5],
        [31.5, 74.5],
        [31.5, 77.5],
        [33.5, 80.5],
      ] as const
    ).map(([x, y]) => ({ kind: 'dummy', x, y })),
    ...(
      [
        [36, 70],
        [40, 70],
        [44, 70],
      ] as const
    ).map(([x, y]) => ({ kind: 'rack', x, y })),
    ...(
      [
        [46.5, 73.5],
        [37, 82.5],
      ] as const
    ).map(([x, y]) => ({ kind: 'drum', x, y })),
    ...(
      [
        [52, 33.5],
        [55, 33.5],
        [60.5, 36.5],
        [100, 33.5],
      ] as const
    ).map(([x, y]) => ({ kind: 'nets', x, y })),
    ...(
      [
        [62, 40],
        [63, 74],
        [52, 70],
      ] as const
    ).map(([x, y]) => ({ kind: 'jars', x, y })),
    ...(
      [
        [67, 51.5],
        [67, 54.5],
      ] as const
    ).map(([x, y]) => ({ kind: 'censer', x, y })),
    ...(
      [
        [97, 94],
        [62, 92],
      ] as const
    ).map(([x, y]) => ({ kind: 'cart', x, y })),
    ...(
      [
        [69, 92],
        [73, 93],
        [27, 133],
        [36, 135],
      ] as const
    ).map(([x, y]) => ({ kind: 'hay', x, y })),
    // ── Dressing (docs/archive/SCENERY_AUDIT.md lane L4; props-sheet groups 03, 04, 11–15, 18, 19, 24). `r`: facing
    // in degrees (0 south, 90 east), `v`: the look. Stall goods (sacks, baskets) are placed by rule.
    // The market street (92–119 × 66–78): lantern strings across it, parasols and rugs in the gaps
    // between the stalls, a rack of hanging goods by the gate, the shop's sign, banners at its west end.
    ...([98.5, 104.5, 110.5, 116.5] as const).map((x) => ({ kind: 'lanternString', x, y: 73.3, r: 90, v: 0 })),
    { kind: 'parasol', x: 107, y: 67.4, r: 0, v: 0 },
    { kind: 'goodsTable', x: 110.2, y: 67.3, r: 0, v: 1 },
    { kind: 'rug', x: 96.6, y: 67.3, r: 0, v: 0 },
    { kind: 'rug', x: 116.3, y: 80.2, r: 180, v: 1 },
    { kind: 'parasol', x: 115.6, y: 77.9, r: 180, v: 1 },
    { kind: 'hangRack', x: 118, y: 66.9, r: 0 },
    { kind: 'sign', x: 101.8, y: 78.6, r: 270 },
    { kind: 'crates', x: 93.4, y: 66.9 },
    { kind: 'barrels', x: 102, y: 81.5 },
    { kind: 'tallBanner', x: 92.6, y: 66.6, r: 270, c: '#a8261f' },
    { kind: 'tallBanner', x: 92.6, y: 78.4, r: 270, c: '#2f3a52' },
    // The plaza: benches facing its middle, lantern posts with hook arms at its edges, banners by the
    // drum tower's passage.
    { kind: 'bench', x: 80.5, y: 66.4, r: 0 },
    { kind: 'bench', x: 71.6, y: 74.5, r: 90 },
    { kind: 'bench', x: 89.4, y: 74.5, r: 270 },
    ...(
      [
        [75.5, 65.2, 0],
        [85.5, 65.2, 0],
        [91.4, 70.5, 270],
        [91.4, 78.5, 270],
        [69.2, 78.5, 90],
      ] as const
    ).map(([x, y, r]) => ({ kind: 'hookLantern', x, y, r, v: 0 })),
    { kind: 'tallBanner', x: 77, y: 83.2, r: 180, c: '#a8261f' },
    { kind: 'tallBanner', x: 84, y: 83.2, r: 180, c: '#a8261f' },
    // The inn's front (door 65, 71, east): tea tables either side of the door, standing lanterns at it,
    // its sign, barrels and sacks by the walls, firewood behind.
    { kind: 'teaTable', x: 67.3, y: 68.2 },
    { kind: 'teaTable', x: 67.3, y: 74.2 },
    { kind: 'standLantern', x: 66.6, y: 69.7 },
    { kind: 'standLantern', x: 66.6, y: 72.3 },
    { kind: 'sign', x: 66.7, y: 76.3, r: 90 },
    { kind: 'barrels', x: 65.3, y: 77.1 },
    { kind: 'sacks', x: 65.4, y: 65.4 },
    { kind: 'woodpile', x: 53.2, y: 73.5, r: 270 },
    // The training ground: sword stands and racks along its north side, targets by the armoury
    // facing the yard, braziers at the sparring platform, a bench to watch from, firewood.
    { kind: 'swords', x: 34.5, y: 65.6, r: 0 },
    { kind: 'swords', x: 42, y: 65.6, r: 0 },
    { kind: 'target', x: 43.5, y: 87.6, r: 180 },
    { kind: 'target', x: 46, y: 87.6, r: 180 },
    { kind: 'brazier', x: 37.3, y: 73.3 },
    { kind: 'brazier', x: 37.3, y: 81.7 },
    { kind: 'brazier', x: 46.7, y: 81.7 },
    { kind: 'bench', x: 35.8, y: 77.5, r: 90 },
    { kind: 'woodpile', x: 41.6, y: 85.8, r: 0 },
    { kind: 'sacks', x: 33, y: 86.2 },
    { kind: 'stele', x: 75, y: 49 },
    { kind: 'lighthouse', x: 34.5, y: 16.5 },
    { kind: 'chest', x: 100.5, y: 6.5 },
    { kind: 'swordstone', x: 118.5, y: 140.5 },
  ],
  // Landmark names are kept from the old map (folk.ts, scenes.ts and professions.ts read them).
  landmarks: {
    wake: [76.5, 11.5],
    pier: [80.5, 11.5],
    pierWest: [71.5, 9.5],
    pierEast: [90.5, 9.5],
    pierEnd: [84.5, 11.5],
    shore: [52.5, 31.5],
    home1: [71.5, 40.5],
    home2: [84.5, 40.5],
    home3: [55.5, 37.5],
    // On the zigzag bridge, a step short of the pavilion (the sketch's spot was open water).
    pavilion: [104.5, 39.5],
    houseN: [83.5, 49.5],
    // The courtyard's east edge on the road (the sketch's 75.5 left the road's far side outside
    // the C3 trigger's 5.5 tiles).
    shrine: [76.5, 53.5],
    shrineTop: [65.0, 53.0],
    inn: [66.5, 71.5],
    elderHouse: [92.5, 60.0],
    shop: [97.5, 77.0],
    trainYard: [46.5, 75.5],
    yardWallTop: [25.5, 85.5],
    pond: [28.5, 72.5],
    houseSW: [73.5, 100.5],
    houseSE: [85.5, 101.5],
    gate: [80.5, 125.5],
    fieldsWest: [50.5, 108.5],
    fieldsEast: [100.5, 101.5],
    pass: [12.5, 57.5],
    quarry: [19.5, 94.5],
    grove: [136.5, 76.5],
    lair: [118.5, 140.5],
    // New with the layout.
    schools: [108.5, 60.5],
    guide: [73.5, 79.5],
    carriage: [93, 93.5],
    stationMaster: [88.5, 89.5],
    // ยายเหมย, keeper of the shrine (canon, year 1): her post in the shrine hall's porch, not spawned yet.
    meiPost: [65.5, 55.5],
  },
  points: {
    plaza: [80.5, 74.5],
    spawn: [80.5, 77.5],
    elder: [84.5, 70.5],
    jail: [89.5, 126.5],
    duel: [135.5, 31.5],
    // The world boss's ring is unused in a village: the arena.
    arena: [135.5, 31.5],
    lair: [118.5, 140.5],
  },
  regions: {
    wolves: [124, 150, 56, 96],
    east: [120, 150, 98, 124],
    bandits: [14, 34, 104, 150],
    west: [31, 48, 69, 86],
    graves: [106, 132, 130, 152],
  },
  portals: [
    { to: 'plains', side: 's', at: [80.5, 154.5] },
    { to: 'mist-peak', side: 'w', at: [5.5, 57.5] },
  ],
  reserved: [
    // The ferry to หมู่เกาะคลื่นคราม (LIVE_ROADMAP M1), inactive until the islands exist.
    { name: 'หมู่เกาะคลื่นคราม', side: 'n', at: [70.5, 8.0] },
  ],
  // rules: the outline takes in the bay out to the islet, the pier where the player wakes, the
  // wave cape and its training rocks (the sketch's ran along the shore, leaving them outside).
  safe: [
    [16, 30],
    [26, 8],
    [46, 8],
    [58, 2],
    [124, 2],
    [150, 12],
    [150, 49],
    [119.5, 51],
    [119.5, 96],
    [113.5, 96],
    [113.5, 127.5],
    [35.5, 127.5],
    [35.5, 91],
    [16, 91],
    [16, 62],
    [16, 52],
  ],
};
